import { useContext, useMemo, useRef } from 'react'
import { WorkspaceContext } from '../WorkspaceContext'
import { useDriveView } from '../DriveViewContext'
import { useUploadEngine } from './UploadEngineContext'
import { useOSFileDrop } from '../../../shared/hooks/useOSFileDrop'
import { canAddResource, canCreateInFolder } from '../../../shared/utils/permissions'
import { findFolderInTree } from '../../../shared/utils/folderTree'

// The "interpret drag events, decide the destination folder" piece: reused
// upload validation/limits/permission logic does the rest via UploadEngineContext.
//
// - Drive mode: always targets the currently-open Drive folder (or room root),
//   regardless of where inside the workspace the drop lands.
// - Editor mode: targets whichever folder widget (a sidebar tree row — every
//   row already carries a data-folderid attribute) is under the cursor at
//   drop time, else room root.
export function useWorkspaceFileDropZone(containerRef: React.RefObject<HTMLElement | null>) {
    const ctx = useContext(WorkspaceContext);
    if (!ctx) throw new Error("useWorkspaceFileDropZone must be used within WorkspaceContext.Provider");
    const { uiMode, memberDetails, roomDetails, Folders } = ctx;
    const { folderId: driveFolderId } = useDriveView();
    const { beginUpload, hoveredDropFolderId, setHoveredDropFolderId } = useUploadEngine();
    const clientId = localStorage.getItem('flashroom_id');

    const hoveredFolderIdRef = useRef<string | null>(null);

    const yourRole = useMemo(() => {
        const isAdmin = memberDetails?.role === 'admin';
        const isOwner = memberDetails?.role === 'owner' || memberDetails?.clientId === roomDetails?.creatorClientId;
        if (isOwner) return 'owner';
        if (isAdmin) return 'admin';
        return 'member';
    }, [memberDetails, roomDetails]);

    const canAdd = canAddResource(memberDetails);

    const resolveEditorTargetFolder = (clientX: number, clientY: number): string | null => {
        const el = document.elementFromPoint(clientX, clientY);
        const folderId = el?.closest('[data-folderid]')?.getAttribute('data-folderid');
        if (!folderId) return null;

        const folder = findFolderInTree(Folders, folderId);
        if (!folder) return null;

        return canCreateInFolder(folder, { clientId, yourRole, canAdd }) ? folder._id : null;
    };

    const updateHoveredFolder = (next: string | null) => {
        if (hoveredFolderIdRef.current === next) return;
        hoveredFolderIdRef.current = next;
        setHoveredDropFolderId(next);
    };

    const handleDragOverPoint = (point: { clientX: number; clientY: number }) => {
        if (uiMode !== 'editor') {
            updateHoveredFolder(null);
            return;
        }
        updateHoveredFolder(resolveEditorTargetFolder(point.clientX, point.clientY));
    };

    const handleDroppedFiles = (files: File[], point: { clientX: number; clientY: number }) => {
        const mode = files.some(f => !!f.webkitRelativePath) ? 'folder' : 'file';

        const targetFolderId = uiMode === 'files'
            ? driveFolderId
            : resolveEditorTargetFolder(point.clientX, point.clientY);

        updateHoveredFolder(null);
        void beginUpload(files, mode, targetFolderId);
    };

    const { isDraggingFiles } = useOSFileDrop({
        containerRef,
        enabled: canAdd,
        onDrop: handleDroppedFiles,
        onDragOverPoint: handleDragOverPoint,
    });

    return {
        isDraggingFiles,
        // Only meaningful mid-drag in editor mode; auto-clears once the drag ends.
        hoveredFolderId: isDraggingFiles ? hoveredDropFolderId : null,
    };
}
